import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { colorFamily, isColorFamily } from "@/office/colors";
import { hasHighlight, readDocument } from "@/office/word/document";

export const lektionFarbeUndHervorhebung: Lesson = {
  id: "farbe-und-hervorhebung",
  title: "Schriftfarbe und Texthervorhebung",
  summary: "Der Unterschied zwischen Schriftfarbe und Textmarker – und wann welche passt.",
  durationMinutes: 8,
  learningGoals: [
    "Schriftfarbe und Texthervorhebung unterscheiden",
    "Beide Werkzeuge gezielt anwenden",
    "Auf ausreichenden Kontrast achten",
  ],
  steps: [
    {
      id: "schriftfarbe",
      title: "Die Schriftfarbe ändern",
      explanation: [
        "Die Schriftfarbe färbt die Buchstaben selbst. Du findest sie im Menüband unter " +
          "\"Start\" beim Buchstaben \"A\" mit dem farbigen Balken darunter. Ein Klick auf " +
          "den kleinen Pfeil daneben öffnet die Farbpalette.",
        "Farbe ist ein starkes Signal – in seriösen Dokumenten reicht meist eine einzige " +
          "Akzentfarbe zusätzlich zu Schwarz. Und bedenke: Auf einem Schwarz-Weiß-Ausdruck " +
          "bleibt von der Farbe nur ein Grauton übrig.",
      ],
      keyPoints: [
        "Die Schriftfarbe färbt die Buchstaben, nicht den Hintergrund.",
        "Eine Akzentfarbe pro Dokument genügt in der Regel.",
      ],
      task: {
        instruction: "Färbe ein einzelnes Wort in deinem Text rot ein.",
        hint: "Wort markieren, dann auf den Pfeil neben dem farbigen \"A\" klicken und ein Rot auswählen.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.runs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const redRun = snapshot.runs.find((run) => isColorFamily(run.color, "rot"));
          if (!redRun) {
            const usedColors = [
              ...new Set(
                snapshot.runs
                  .map((run) => colorFamily(run.color))
                  .filter((family): family is NonNullable<typeof family> => family !== null),
              ),
            ];
            return fail(
              `Noch kein rotes Wort gefunden. Erkannte Schriftfarben: ${
                usedColors.join(", ") || "nur Standardfarbe"
              }.`,
              "Achte darauf, wirklich die Schriftfarbe zu ändern und nicht den Textmarker zu verwenden.",
            );
          }
          return pass(`"${redRun.text}" ist jetzt rot eingefärbt.`);
        },
      },
    },
    {
      id: "hervorhebung",
      title: "Mit dem Textmarker arbeiten",
      explanation: [
        "Die Texthervorhebung – der Stift mit dem gelben Balken im Menüband – legt eine " +
          "Farbfläche hinter den Text, wie ein echter Textmarker auf Papier.",
        "Sie eignet sich hervorragend für Arbeitsstände: offene Stellen markieren, " +
          "Rückfragen kennzeichnen, Entwürfe kommentieren. In der finalen Fassung eines " +
          "Dokuments sollte in der Regel keine Hervorhebung mehr übrig sein.",
      ],
      keyPoints: [
        "Textmarker = Farbe hinter dem Text, Schriftfarbe = Farbe der Buchstaben.",
        "Zum Entfernen im Menü der Hervorhebung \"Keine Farbe\" wählen.",
      ],
      task: {
        instruction: "Hebe ein Wort mit dem gelben Textmarker hervor.",
        hint: "Wort markieren, dann auf den Textmarker klicken. Über den Pfeil daneben wählst du die Farbe.",
        check: async () => {
          const snapshot = await readDocument();

          const highlighted = snapshot.runs.filter(hasHighlight);
          if (highlighted.length === 0) {
            return fail(
              "Es ist noch kein Wort hervorgehoben.",
              "Der Textmarker sitzt im Menüband rechts neben der Schriftfarbe.",
            );
          }

          const yellow = highlighted.find((run) => isColorFamily(run.highlightColor, "gelb"));
          if (!yellow) {
            return fail(
              `Es ist etwas hervorgehoben, aber nicht in Gelb (erkannt: ${
                colorFamily(highlighted[0].highlightColor) ?? "unbekannte Farbe"
              }).`,
              "Wähle über den Pfeil neben dem Textmarker die gelbe Farbe aus.",
            );
          }
          return pass(`"${yellow.text}" ist jetzt gelb hervorgehoben.`);
        },
      },
    },
    {
      id: "kontrast",
      title: "Auf Lesbarkeit achten",
      explanation: [
        "Farbe darf die Lesbarkeit nie verschlechtern. Der Klassiker unter den Fehlern " +
          "ist heller Text auf hellem Grund – gelbe Schrift auf weißem Papier ist " +
          "praktisch unlesbar, auf dem Bildschirm wie im Ausdruck.",
        "Denk außerdem daran, dass etwa jede zwölfte Person eine Farbsehschwäche hat. " +
          "Verlasse dich deshalb nie allein auf Farbe, um etwas zu kennzeichnen – " +
          "kombiniere sie mit fetter Schrift oder einer klaren Beschriftung.",
      ],
      keyPoints: [
        "Dunkle Schrift auf hellem Grund bleibt die sicherste Kombination.",
        "Farbe nie als einziges Unterscheidungsmerkmal einsetzen.",
      ],
      task: {
        instruction:
          "Prüfe dein Dokument: Es darf kein Wort in einer hellen Schriftfarbe (Gelb oder " +
          "Weiß) ohne farbige Hervorhebung dahinter stehen.",
        hint: "Falls du damit experimentiert hast: Markiere die Stelle und setze die Schriftfarbe zurück auf Automatisch.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.runs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const unreadable = snapshot.runs.filter((run) => {
            const family = colorFamily(run.color);
            const isLight = family === "gelb" || family === "weiß";
            return isLight && !hasHighlight(run);
          });

          return fromCriteria(
            [
              criterion(
                "Kein heller Text ohne farbigen Hintergrund",
                unreadable.length === 0,
              ),
            ],
            {
              success: "Dein Dokument ist durchgehend gut lesbar.",
              failure: `${unreadable.length} ${
                unreadable.length === 1 ? "Stelle ist" : "Stellen sind"
              } zu hell für den weißen Hintergrund: ${unreadable
                .slice(0, 3)
                .map((run) => `"${run.text}"`)
                .join(", ")}.`,
              hint: "Markiere die Stelle und wähle bei der Schriftfarbe \"Automatisch\".",
            },
          );
        },
      },
    },
  ],
};
